/**
 * useBluetoothRoomBridge Hook
 *
 * Bridges Bluetooth mesh messages with room CRDT state.
 * When online transport fails, messages can still propagate via Bluetooth.
 */

import { useEffect, useCallback, useRef } from 'react'
import * as Y from 'yjs'
import {
  broadcastMessage,
  setMessageHandler,
  getBluetoothStatus,
  getConnectedPeers,
} from '@/lib/bluetooth/mesh'
import type { BluetoothMessage } from './useBluetooth'
import type { RoomDocument } from '@/lib/sync/crdt'

// BLE MTU fragment handling (matching useBluetooth.ts)
const BLE_MTU = 512
const FRAGMENT_HEADER_SIZE = 5

interface UseBluetoothRoomBridgeOptions {
  roomId: string
  roomDoc: RoomDocument | null
  enabled: boolean
}

/**
 * Encode a message for Bluetooth transmission
 */
function encodeMessage(message: BluetoothMessage): Uint8Array {
  const json = JSON.stringify(message)
  return new TextEncoder().encode(json)
}

/**
 * Fragment a large message for BLE MTU
 */
function fragmentForBLE(data: Uint8Array): Uint8Array[] {
  const maxPayloadSize = BLE_MTU - FRAGMENT_HEADER_SIZE
  const totalFragments = Math.ceil(data.length / maxPayloadSize)
  const fragments: Uint8Array[] = []

  for (let i = 0; i < totalFragments; i++) {
    const start = i * maxPayloadSize
    const end = Math.min(start + maxPayloadSize, data.length)
    const payload = data.slice(start, end)

    const fragment = new Uint8Array(FRAGMENT_HEADER_SIZE + payload.length)
    fragment[0] = 0x01 // Fragment type
    fragment[1] = (totalFragments >> 8) & 0xff
    fragment[2] = totalFragments & 0xff
    fragment[3] = (i >> 8) & 0xff
    fragment[4] = i & 0xff
    fragment.set(payload, FRAGMENT_HEADER_SIZE)

    fragments.push(fragment)
  }

  return fragments
}

/**
 * Reassemble fragments into complete message
 */
class FragmentReassembler {
  private fragments = new Map<string, Map<number, Uint8Array>>()
  private expectedCounts = new Map<string, number>()

  process(peerId: string, data: Uint8Array): Uint8Array | null {
    // Check if this is a fragment (type 0x01) or complete message
    if (data.length < FRAGMENT_HEADER_SIZE || data[0] !== 0x01) {
      return data // Complete message
    }

    const total = ((data[1] ?? 0) << 8) | (data[2] ?? 0)
    const index = ((data[3] ?? 0) << 8) | (data[4] ?? 0)
    const payload = data.slice(FRAGMENT_HEADER_SIZE)

    if (!this.fragments.has(peerId)) {
      this.fragments.set(peerId, new Map())
    }

    const peerFragments = this.fragments.get(peerId)!
    peerFragments.set(index, payload)
    this.expectedCounts.set(peerId, total)

    // Check if complete
    if (peerFragments.size === total) {
      let totalSize = 0
      for (let i = 0; i < total; i++) {
        totalSize += peerFragments.get(i)?.length ?? 0
      }

      const assembled = new Uint8Array(totalSize)
      let offset = 0
      for (let i = 0; i < total; i++) {
        const frag = peerFragments.get(i)
        if (frag) {
          assembled.set(frag, offset)
          offset += frag.length
        }
      }

      this.fragments.delete(peerId)
      this.expectedCounts.delete(peerId)
      return assembled
    }

    return null // Still waiting for fragments
  }
}

export function useBluetoothRoomBridge({
  roomId,
  roomDoc,
  enabled,
}: UseBluetoothRoomBridgeOptions) {
  const reassemblerRef = useRef(new FragmentReassembler())
  const lastBroadcastRef = useRef<number>(0)

  // Handle incoming Bluetooth messages and sync to CRDT
  const handleBluetoothMessage = useCallback(
    (data: Uint8Array, fromPeerId: string) => {
      if (!roomDoc || !enabled) return

      const ydoc = roomDoc.doc // Get the underlying Y.Doc

      try {
        const assembled = reassemblerRef.current.process(fromPeerId, data)
        if (!assembled) return // Still waiting for fragments

        const json = new TextDecoder().decode(assembled)
        const message = JSON.parse(json) as BluetoothMessage

        // Only process messages for this room
        if (message.roomId !== roomId) return

        // Handle different message types
        if (message.type === 'state_sync') {
          // Apply Yjs state update from Bluetooth peer
          const stateUpdate = Uint8Array.from(
            atob(message.payload)
              .split('')
              .map((c) => c.charCodeAt(0))
          )
          Y.applyUpdate(ydoc, stateUpdate, 'bluetooth')
          console.log('[BluetoothBridge] Applied state sync from', fromPeerId)
        } else if (message.type === 'room_message') {
          // Individual message - add to messages array
          const msgData = JSON.parse(message.payload)

          // Check for duplicate
          const existing = roomDoc.messages.toArray().find((m) => m.id === msgData.id)
          if (!existing) {
            roomDoc.messages.push([msgData])
            console.log('[BluetoothBridge] Added message from Bluetooth peer')
          }
        }
      } catch (err) {
        console.error('[BluetoothBridge] Failed to process message:', err)
      }
    },
    [roomDoc, roomId, enabled]
  )

  // Set up Bluetooth message handler
  useEffect(() => {
    if (!enabled) return

    setMessageHandler(handleBluetoothMessage)
    console.log('[BluetoothBridge] Message handler registered for room', roomId)

    return () => {
      setMessageHandler(() => {})
    }
  }, [enabled, handleBluetoothMessage, roomId])

  // Broadcast CRDT updates via Bluetooth
  useEffect(() => {
    if (!roomDoc || !enabled) return

    const ydoc = roomDoc.doc // Get the underlying Y.Doc

    const handleUpdate = async (update: Uint8Array, origin: unknown) => {
      // Don't re-broadcast updates we received from Bluetooth
      if (origin === 'bluetooth') return

      // Rate limit broadcasts
      const now = Date.now()
      if (now - lastBroadcastRef.current < 100) return
      lastBroadcastRef.current = now

      // Check if Bluetooth is connected
      const status = getBluetoothStatus()
      const peers = getConnectedPeers()
      if (status !== 'connected' || peers.length === 0) return

      try {
        // Encode update as base64 for JSON transmission
        const base64Update = btoa(String.fromCharCode(...update))

        const message: BluetoothMessage = {
          type: 'state_sync',
          roomId,
          payload: base64Update,
          timestamp: now,
        }

        const encoded = encodeMessage(message)
        const fragments = fragmentForBLE(encoded)

        for (const fragment of fragments) {
          await broadcastMessage(fragment)
          if (fragments.length > 1) {
            await new Promise((r) => setTimeout(r, 20))
          }
        }

        console.log('[BluetoothBridge] Broadcast CRDT update to', peers.length, 'peers')
      } catch (err) {
        console.error('[BluetoothBridge] Failed to broadcast update:', err)
      }
    }

    ydoc.on('update', handleUpdate)
    return () => {
      ydoc.off('update', handleUpdate)
    }
  }, [roomDoc, roomId, enabled])

  // Broadcast a specific room message via Bluetooth
  const broadcastRoomMessage = useCallback(
    async (messageData: Record<string, unknown>) => {
      if (!enabled) return

      const status = getBluetoothStatus()
      const peers = getConnectedPeers()
      if (status !== 'connected' || peers.length === 0) return

      try {
        const message: BluetoothMessage = {
          type: 'room_message',
          roomId,
          payload: JSON.stringify(messageData),
          timestamp: Date.now(),
        }

        const encoded = encodeMessage(message)
        const fragments = fragmentForBLE(encoded)

        for (const fragment of fragments) {
          await broadcastMessage(fragment)
          if (fragments.length > 1) {
            await new Promise((r) => setTimeout(r, 20))
          }
        }

        console.log('[BluetoothBridge] Broadcast room message via Bluetooth')
      } catch (err) {
        console.error('[BluetoothBridge] Failed to broadcast message:', err)
      }
    },
    [roomId, enabled]
  )

  return {
    broadcastRoomMessage,
    isBluetoothBridgeActive: enabled && getBluetoothStatus() === 'connected',
  }
}
